<template>
  <div class="border-2 border-gray-800">
    <div class="overflow-hidden text-base bg-gray-800 text-white px-4 py-3">
      <div class="float-left">
        Employment {{ index + 1 }}
      </div>

      <div v-if="(index > 0 && !employment.previous) ||
                 (employment.previous && !isCurrentLessThanThreeYears) ||
                 (employment.previous && isCurrentLessThanThreeYears && numPreviousEmployments > 1)"
        class="float-right">
        <a href="#"
          @click.prevent="deleteEmployment(employment.id, index)"
          class="no-underline text-sm text-white hover:underline">
          <span class="">Delete</span>
        </a>
      </div>
    </div>

    <div class="p-4">
      <div class="flex flex-wrap">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <EmploymentStatusSelector
            propertyKey="status"
            :value="employment.status"
            objectType="employment"
            :index="index"
            :updateValue="updateValue"
            :required="true"></EmploymentStatusSelector>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <div class="label-standard">Self employment type</div>

          <div class="form-line">
            <select
              :value="employment.sub_status != null ? employment.sub_status : -1"
              v-on:change="updateValue({
                objectType: 'employment',
                index     : index,
                key       : 'sub_status',
                value     : $event.target.value
              })"
              class="select-standard">
              <option value="-1" disabled="">Select an option...</option>
              <option value="0">Sole trader</option>
              <option value="1">Partnership</option>
              <option value="2">Company director</option>
            </select>
          </div>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText label="Business name"
            propertyKey="name"
            objectType="employment"
            :index="index"
            :value="employment.name"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputText label="Nature of business"
            propertyKey="nature"
            objectType="employment"
            :index="index"
            :value="employment.nature"
            :updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputABN label="Employer's ABN"
            propertyKey="abn"
            objectType="employment"
            :index="index"
            :value="employment.abn"
            :updateValue="updateValue"></InputABN>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputDatePicker
            label="Date of ABN registration"
            propertyKey="registered_abn_at"
            objectType="employment"
            :index="index"
            :maxDate="today"
            :value="employment.registered_abn_at"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="overflow-hidden mt-12">
        <div class="float-left text-base uppercase font-semibold text-gray-700">
          Business Address
        </div>
      </div>
       <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 flex flex-wrap">
          <div class="w-1/3 pr-2">
            <BuildingTypeSelector
              propertyKey="building_type"
              objectType="address"
              :index="index"
              :value="employment.building_type"
              :updateValue="updateValue" />
          </div>

          <div class="w-1/3 pr-2">
            <InputText 
              label="Unit number"
              propertyKey="unit_number"
              objectType="address"
              :index="index"
              :value="employment.unit_number"
              :updateValue="updateValue" />
          </div>

          <div class="w-1/3">
            <InputText 
              label="Street number"
              propertyKey="street_number"
              objectType="address"
              :index="index"
              :value="employment.street_number"
              :updateValue="updateValue" />
          </div>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0 flex flex-wrap">
          <div class="w-3/4 pr-2">
            <InputText 
              label="Street name"
              propertyKey="street_name"
              objectType="address"
              :index="index"
              :value="employment.street_name"
              :updateValue="updateValue" />

            <div class="mt-1 text-right" v-if="employment.street_name != null && employment.street_name.length > 0">
              <a href="#" class="text-gray-700 hover:underline" @click.prevent="$modal.show('map-modal', { 
                address : getAddressText() 
              })">Show this address on map</a>
            </div>
          </div>

          <div class="w-1/4 pl-1">
            <StreetTypeSelector
              propertyKey="street_type"
              objectType="address"
              :index="index"
              :value="employment.street_type"
              :updateValue="updateValue" />
          </div>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText 
            label="Suburb" 
            objectType="address"
            propertyKey="suburb"
            :index="index"
            :value="employment.suburb"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputPostcode 
            label="Postcode" 
            objectType="address"
            propertyKey="postcode"
            :index="index"
            :value="employment.postcode"
            :updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 mt-8 lg:mt-0 pr-0 lg:pr-3">
          <StateSelector
            objectType="address"
            propertyKey="state_id"
            :index="index"
            :value="employment.state_id"
            :updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <CountrySelector
            objectType="address"
            propertyKey="country_id"
            :index="index"
            :value="employment.country_id"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <div class="label-standard">Are you registered for GST?</div>

          <div class="radio-line flex items-center">
            <InputRadio
              :name="'inputGst-' + index"
              :id="'inputGstYes-' + index"
              propertyKey="gst"
              objectType="employment"
              :index="index"
              value="1"
              :checked="employment.gst == 1"
              :updateValue="updateValue"></InputRadio>

            <label class="ml-2 mr-8 text-base" :for="'inputGstYes-' + index">Yes</label>

            <InputRadio
              :name="'inputGst-' + index"
              :id="'inputGstNo-' + index"
              propertyKey="gst"
              objectType="employment"
              :index="index"
              value="0"
              :checked="employment.gst == 0"
              :updateValue="updateValue"></InputRadio>

            <label class="ml-2 mr-8 text-base" :for="'inputGstNo-' + index">No</label>
          </div>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0" v-if="employment.gst == 1">
          <InputDatePicker
            label="Date of GST registration"
            propertyKey="registered_gst_at"
            objectType="employment"
            :index="index"
            :maxDate="today"
            :value="employment.registered_gst_at"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputDatePicker
            label="Date started"
            propertyKey="date_started_at"
            objectType="employment"
            :index="index"
            :value="employment.date_started_at"
            :updateValue="updateValue" />

          <div class="mt-2 text-sm text-red-500" v-if="isStartedLaterThanEnded">
            This should be earlier than date ended
          </div>
        </div>

        <div v-if="employment.previous" class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputDatePicker
            label="Date ended"
            propertyKey="date_ended_at"
            objectType="employment"
            :index="index"
            :value="employment.date_ended_at"
            :updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <div class="text-sm">
            How long there?
          </div>

          <div class="mt-2">
            <div class="w-full border bg-gray-200 px-3" style="height:38px;line-height:38px;">
              {{ getEmploymentDuration(employment) }}
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputDollarValue 
            label="Current year net profit"
            propertyKey="year_profit"
            objectType="employment"
            :value="employment.year_profit"
            :index="index"
            :updateValue="updateValue"/>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputDollarValue 
            label="Last year net profit"
            propertyKey="last_year_profit"
            objectType="employment"
            :value="employment.last_year_profit"
            :index="index"
            :updateValue="updateValue"/>
        </div>
      </div>

      <div class="mt-12">
        <Accountant></Accountant>
      </div>
    </div>
  </div>
</template>

<script>

  import InputText                from '@/shared/components/form/InputText'
  import InputABN                 from '@/shared/components/form/InputABN'
  import InputDollarValue         from '@/shared/components/form/InputDollarValue'
  import RequiredAsterisk         from '@/shared/components/form/RequiredAsterisk'
  import InputDatePicker          from '@/shared/components/form/InputDatePicker'
  import InputRadio               from '@/shared/components/form/InputRadio'
  import InputPostcode            from '@/shared/components/form/InputPostcode'
  import StateSelector            from '@/shared/components/form/StateSelector'
  import CountrySelector          from '@/shared/components/form/CountrySelector'
  import StreetTypeSelector       from '@/shared/components/form/StreetTypeSelector'
  import Accountant               from '../Accountant'
  import EmploymentStatusSelector from '../EmploymentStatusSelector'
  import BuildingTypeSelector     from '@/shared/components/form/BuildingTypeSelector'

  export default {

    components: {
      InputText,
      InputABN,
      InputDollarValue,
      RequiredAsterisk,
      InputDatePicker,
      InputRadio,
      StateSelector,
      CountrySelector,
      Accountant,
      EmploymentStatusSelector,
      BuildingTypeSelector,
      InputPostcode,
      StreetTypeSelector
    },

    props : {
      employment : {
        type    : Object,
        required: true
      },

      updateValue : {
        type : Function,
        required : true,
      },

      objectType : {
        type : String,
        required : false
      },

      index : {
        type : Number,
        required : false
      },

      deleteEmployment : {
        type    : Function,
        required: false
      },

      isCurrentLessThanThreeYears : {
        type : Boolean,
        required : false
      },

      getEmploymentDuration : {
        type : Function,
        required : false,
      },

      numPreviousEmployments : {
        type : Number,
        required : false
      },
    },

    computed : {
      isStartedLaterThanEnded() {
        if(this.employment.date_started_at != null && this.employment.date_ended_at != null && this.employment.date_started_at > this.employment.date_ended_at) {
          return true
        }

        return false
      }
    }
  }
</script>